import React, { useState, useEffect } from "react";
import { Box, Typography, Tab, Tabs, useMediaQuery, Grid, CircularProgress } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { TwitterEmbed, LinkedInEmbed } from "react-social-media-embed";

const API_URL =
  "https://dev.ienergydigital.com/task-api/api/cms/entries/?type=social_media";

const MainContainer = styled(Box)(({ theme }) => ({
  background: "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "60px 24px",
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    width: "95%",
    height: "80%",
    left: "180%",
    top: "10%",
    bottom: "10%",
    transform: "translateX(-100%)",
    background:
      "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
    filter: "blur(100px)",
    zIndex: 0,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    width: "95%",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const GradientBorderBox = styled(Box)(({ theme }) => ({
  background:
    "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.2) 0%, rgba(110, 191, 244, 0.0447917) 77.08%, rgba(70, 144, 213, 0) 100%)",
  position: "relative",
  borderRadius: "16px",
  padding: theme.spacing(3),
  overflow: "hidden",
  height: "auto",
  minHeight: "590px",
  display: "flex",
  flexDirection: "column",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "16px",
    padding: "0.92px",
    background:
      "linear-gradient(90deg, #98F9FF 0%, rgba(255, 255, 255, 0) 50%, #EABFFF 100%)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    pointerEvents: "none",
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  width: "100%",
  marginBottom: theme.spacing(3),
  "& .MuiTabs-indicator": {
    display: "none",
  },
  "& .MuiTabs-flexContainer": {
    justifyContent: "flex-start",
  },
  "& .MuiTab-root": {
    color: "#FFFFFF80",
    fontWeight: 500,
    fontSize: "16px",
    textTransform: "none",
    "&.Mui-selected": {
      color: "#FFFFFF",
      fontWeight: 600,
    },
  },
}));

const CardContainer = styled(Box)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "12px",
  overflow: "hidden",
  transition: "transform 0.3s ease",
  height: "100%",
  "&:hover": {
    transform: "scale(1.03)",
  },
  cursor: "pointer",
}));

const ImageContainer = styled(Box)({
  width: "100%",
  height: "200px",
  overflow: "hidden",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
}));

const NavigationButton = styled("button")(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255, 255, 255, 0.1)",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  cursor: "pointer",
  fontSize: "18px",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.2)",
  },
}));

// ---- helpers to normalize the CMS payload ----

// Some entries carry contentJson.platform directly (post entries),
// others only have a slug like "facebook" / "twitter-post-01".
// Fall back to the slug prefix when platform isn't present.
const getPlatform = (entry) => {
  const explicit = entry?.contentJson?.platform;
  if (explicit) return String(explicit).toLowerCase();

  const slug = entry?.slug || "";
  const prefix = slug.split("-")[0];
  return prefix ? prefix.toLowerCase() : null;
};

// Normalize a raw entry into the shape the card UI expects.
const normalizeCard = (entry) => {
  const c = entry?.contentJson || {};
  return {
    id: entry.id,
    image: c.thumbnail || "",
    title: c.name || c.caption || "",
    desc: c.caption || "",
    timeline: c.platform ? c.platform.charAt(0).toUpperCase() + c.platform.slice(1) : "",
    date: c.postDate || "",
    postLink: c.postLink || "",
  };
};

const StayConnected = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const socialPlatforms = ["LinkedIn", "Instagram", "Facebook", "Twitter"];
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const [showEmbed, setShowEmbed] = useState(false);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSocialEntries = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(API_URL);
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }

        const json = await res.json();

        if (!json?.success) {
          throw new Error(json?.message || "Failed to fetch social media entries");
        }

        if (isMounted) {
          setEntries(json?.data?.entries || []);
        }
      } catch (err) {
        console.error("Error fetching social media entries:", err);
        if (isMounted) {
          setError(err.message || "Something went wrong");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSocialEntries();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowEmbed(true), 1000); // Delay 1 second
    return () => clearTimeout(timer);
  }, []);

  // Reset carousel position whenever the active tab or data changes,
  // so we don't end up with an out-of-range activeIndex.
  useEffect(() => {
    setActiveIndex(0);
  }, [currentTab, entries]);

  const handleNewsDetails = (postLink) => {
    if (postLink) {
      window.open(postLink, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(`/newsroom-details`);
  };

  const handleChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // ---- derive per-platform data from the fetched entries ----

  const linkedinPosts = entries
    .filter((e) => getPlatform(e) === "linkedin")
    .map((e) => e.contentJson?.postLink)
    .filter(Boolean);

  const twitterPosts = entries
    .filter((e) => getPlatform(e) === "twitter")
    .map((e) => e.contentJson?.postLink)
    .filter(Boolean);

  const instagramCards = entries
    .filter((e) => getPlatform(e) === "instagram")
    .map(normalizeCard);

  const facebookCards = entries
    .filter((e) => getPlatform(e) === "facebook")
    .map(normalizeCard);

  const currentCardData =
    currentTab === 1 ? instagramCards : currentTab === 2 ? facebookCards : [];

  const handleSlideChange = (direction) => {
    const listLength =
      currentTab === 0
        ? linkedinPosts.length
        : currentTab === 3
        ? twitterPosts.length
        : currentCardData.length;

    if (!listLength) return;

    setActiveIndex((prev) =>
      direction === "prev"
        ? prev === 0
          ? listLength - 1
          : prev - 1
        : prev === listLength - 1
        ? 0
        : prev + 1
    );
  };

  const getVisibleSlice = (list) => {
    if (!list.length) return [];
    const cardsToShow = isMobile ? 1 : Math.min(3, list.length);
    const visible = [];
    for (let i = 0; i < cardsToShow; i++) {
      const index = (activeIndex + i) % list.length;
      visible.push(list[index]);
    }
    return visible;
  };

  const visibleLinkedinPosts = getVisibleSlice(linkedinPosts);
  const visibleTwitterPosts = getVisibleSlice(twitterPosts);
  const visibleCards = getVisibleSlice(currentCardData);

  const renderEmptyState = (label) => (
    <Grid item xs={12}>
      <Typography sx={{ color: "#8C8CC2", textAlign: "center", py: 6 }}>
        No {label} posts available right now.
      </Typography>
    </Grid>
  );

  return (
    <MainContainer>
      <ContentWrapper>
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          align="center"
          sx={{
            color: "#5A5CFF",
            fontWeight: "bold",
            fontSize: { xs: "28px", md: "40px" },
            marginBottom: "40px",
          }}
        >
          Stay Connected, Stay Inspired
        </Typography>

        <Typography
          variant="body1"
          gutterBottom
          align="center"
          sx={{
            color: "#8C8CC2",
            maxWidth: "800px",
            mx: "auto",
            mb: 5,
            px: 2,
            fontSize: "18px",
            marginBottom: "80px",
          }}
        >
          Join the conversation and stay up to date with the latest trends,
          updates, and success stories from iEnergy. Follow us on our social
          channels and be part of our growing community!
        </Typography>

        <GradientBorderBox>
          <Box display={"flex"} flexDirection={isMobile ? "column" : "row"}>
            <StyledTabs
              value={currentTab}
              onChange={handleChange}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
            >
              {socialPlatforms?.map((platform, index) => (
                <Tab key={platform} label={platform} value={index} />
              ))}
            </StyledTabs>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                width: "100%",
                justifyContent: isMobile ? "center" : "flex-end",
                mb: 4,
              }}
            >
              <NavigationButton onClick={() => handleSlideChange("prev")}>
                &lt;
              </NavigationButton>
              <NavigationButton onClick={() => handleSlideChange("next")}>
                &gt;
              </NavigationButton>
            </Box>
          </Box>

          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
              }}
            >
              <CircularProgress sx={{ color: "#61D1FA" }} />
            </Box>
          ) : error ? (
            <Box sx={{ flex: 1, textAlign: "center", py: 8 }}>
              <Typography sx={{ color: "#FF8A8A" }}>
                Couldn't load social media posts. Please try again later.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {currentTab === 0 ? (
                // LinkedIn content
                visibleLinkedinPosts.length ? (
                  visibleLinkedinPosts.map((postUrl, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`${postUrl}-${index}`}>
                      <Box
                        sx={{
                          background: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "12px",
                          overflow: "hidden",
                          height: "100%",
                          padding: 2,
                        }}
                      >
                        {showEmbed && (
                          <LinkedInEmbed postUrl={postUrl} width="100%" />
                        )}
                      </Box>
                    </Grid>
                  ))
                ) : (
                  renderEmptyState("LinkedIn")
                )
              ) : currentTab === 3 ? (
                // Twitter content
                visibleTwitterPosts.length ? (
                  visibleTwitterPosts.map((tweetUrl, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`${tweetUrl}-${index}`}>
                      <Box
                        sx={{
                          background: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "12px",
                          overflow: "hidden",
                          height: "100%",
                          padding: 2,
                        }}
                      >
                        <TwitterEmbed url={tweetUrl} width="100%" />
                      </Box>
                    </Grid>
                  ))
                ) : (
                  renderEmptyState("Twitter")
                )
              ) : // Instagram (1) / Facebook (2) content, rendered as cards
              visibleCards.length ? (
                visibleCards.map((card, index) => (
                  <Grid item xs={12} md={4} key={`${card.id}-${index}`}>
                    <CardContainer onClick={() => handleNewsDetails(card.postLink)}>
                      <ImageContainer>
                        <img src={card.image} alt={card.title || "social post"} />
                      </ImageContainer>
                      <CardContent>
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Typography sx={{ color: "#8C8CC2", fontSize: "14px" }}>
                            {card.timeline}
                          </Typography>
                          <Typography sx={{ color: "#8C8CC2", fontSize: "14px" }}>
                            {card.date}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            color: "#fff",
                            fontSize: { xs: 20, md: 24 },
                            fontWeight: 600,
                            mb: 2,
                          }}
                        >
                          {card.title}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: "#B0B0B0",
                            fontSize: { xs: 14, md: 16 },
                            lineHeight: 1.6,
                            mb: 3,
                          }}
                        >
                          {card.desc}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#61D1FA",
                            fontSize: 14,
                            mb: 2,
                          }}
                        >
                          Read More →
                        </Typography>
                      </CardContent>
                    </CardContainer>
                  </Grid>
                ))
              ) : (
                renderEmptyState(socialPlatforms[currentTab])
              )}
            </Grid>
          )}
        </GradientBorderBox>
      </ContentWrapper>
    </MainContainer>
  );
};

export default StayConnected;